import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Map, ChevronDown, ChevronUp,
  CheckCircle2, Circle, Target, Zap, Award, TrendingUp,
  Loader2, RefreshCw, Lightbulb, BarChart3, ChevronRight,
  Settings2, Plus, User, CalendarDays, School, AlertTriangle, ShieldCheck
} from 'lucide-react';
import { getLatestRoadmap, generateRoadmap, toggleRoadmapAction, getCampuses, downloadEvidencePack, formatApiError } from '../services/api';
import { ActionMetaEditor, AddActionModal, DriftPanel, STATUS_OPTIONS, CATEGORY_LABELS, statusOf, isOverdue } from '../components/ActionTracker';

const PHASE_CONFIG = {
  0: { icon: Zap, color: '#F59E0B', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', check: 'text-amber-500', progressBar: 'bg-amber-500' },
  1: { icon: Target, color: '#2563EB', bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', check: 'text-blue-500', progressBar: 'bg-blue-500' },
  2: { icon: Award, color: '#7C3AED', bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700', check: 'text-violet-500', progressBar: 'bg-violet-500' },
};

const PRIORITY_STYLES = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-600' };

function ActionItem({ action, meta, status, campuses, onToggle, onMetaSaved, phaseConfig, toggling }) {
  const [editing, setEditing] = useState(false);
  const isComplete = status === 'done';
  const overdue = isOverdue({ ...meta, status });
  const statusCfg = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  const campusName = campuses.find((c) => c.id === meta?.campus_id)?.name;

  return (
    <div className={`border rounded-xl p-5 transition-all ${isComplete ? 'border-slate-200 bg-slate-50 opacity-75' : overdue ? 'border-red-300 bg-white' : `${phaseConfig.border} bg-white hover:shadow-sm`}`} data-testid={`action-${action.id}`}>
      <div className="flex items-start gap-4">
        <button onClick={() => onToggle(action.id)} disabled={toggling === action.id} className="mt-0.5 flex-shrink-0 transition-transform hover:scale-110" data-testid={`toggle-${action.id}`}>
          {toggling === action.id ? <Loader2 className="w-6 h-6 text-slate-400 animate-spin" /> : isComplete ? <CheckCircle2 className={`w-6 h-6 ${phaseConfig.check}`} /> : <Circle className="w-6 h-6 text-slate-300 hover:text-slate-400" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${phaseConfig.badge}`}>{CATEGORY_LABELS[action.category] || action.category}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${PRIORITY_STYLES[action.priority] || PRIORITY_STYLES.medium}`}>{action.priority}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusCfg.cls}`} data-testid={`status-badge-${action.id}`}>{statusCfg.label}</span>
            {overdue && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-600 text-white flex items-center gap-1" data-testid={`overdue-${action.id}`}><AlertTriangle className="w-3 h-3" /> Overdue</span>}
            {action.custom && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-white">Custom</span>}
            {action.current_score != null && <span className="text-xs text-slate-400">{action.current_score}/5 → {action.target_score}/5</span>}
          </div>

          <h4 className={`font-semibold text-slate-900 mb-1.5 ${isComplete ? 'line-through text-slate-500' : ''}`}>{action.item}</h4>
          <p className={`text-sm leading-relaxed mb-2 ${isComplete ? 'text-slate-400' : 'text-slate-600'}`}>{action.recommendation}</p>

          {action.rationale && !isComplete && (
            <p className="text-xs text-slate-400 italic flex items-start gap-1 mb-2"><Lightbulb className="w-3 h-3 mt-0.5 flex-shrink-0" />{action.rationale}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="flex items-center gap-1" data-testid={`owner-display-${action.id}`}><User className="w-3 h-3" />{meta?.owner || <span className="text-amber-600 font-medium">Unassigned</span>}</span>
            <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}><CalendarDays className="w-3 h-3" />{meta?.due_date ? new Date(meta.due_date).toLocaleDateString() : 'No due date'}</span>
            {campusName && <span className="flex items-center gap-1"><School className="w-3 h-3" />{campusName}</span>}
            {meta?.notes && <span className="text-slate-400 truncate max-w-xs">“{meta.notes}”</span>}
            <button onClick={() => setEditing(!editing)} className="ml-auto flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium" data-testid={`manage-${action.id}`}>
              <Settings2 className="w-3 h-3" /> {editing ? 'Close' : 'Manage'}
            </button>
          </div>

          {editing && <ActionMetaEditor action={action} meta={meta} campuses={campuses} onSaved={(id, m, completed) => { onMetaSaved(id, m, completed); setEditing(false); }} />}
        </div>
      </div>
    </div>
  );
}

function PhaseSection({ phase, index, actionMeta, completed, campuses, onToggle, onMetaSaved, toggling, onAddAction }) {
  const [expanded, setExpanded] = useState(index === 0);
  const config = PHASE_CONFIG[index] || PHASE_CONFIG[0];
  const PhaseIcon = config.icon;
  const actions = phase.actions || [];
  const completedInPhase = actions.filter((a) => statusOf(actionMeta, completed, a.id) === 'done').length;
  const phaseProgress = actions.length > 0 ? Math.round((completedInPhase / actions.length) * 100) : 0;
  const isPhaseComplete = completedInPhase === actions.length && actions.length > 0;

  return (
    <div className={`border ${isPhaseComplete ? 'border-emerald-200' : config.border} rounded-2xl overflow-hidden transition-all`} data-testid={`phase-${index}`}>
      <button onClick={() => setExpanded(!expanded)} className={`w-full px-6 py-5 flex items-center justify-between transition-all ${isPhaseComplete ? 'bg-emerald-50' : config.bg}`}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: isPhaseComplete ? '#059669' : config.color }}>
            {isPhaseComplete ? <CheckCircle2 className="w-6 h-6 text-white" /> : <PhaseIcon className="w-6 h-6 text-white" />}
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="font-outfit text-lg font-semibold text-slate-900">{phase.name}</h3>
              <span className="text-xs font-medium text-slate-500 bg-white px-2 py-0.5 rounded-full">{completedInPhase}/{actions.length}</span>
            </div>
            <p className="text-sm text-slate-500">{phase.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:block w-32">
            <div className="h-2 bg-white/60 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${isPhaseComplete ? 'bg-emerald-500' : config.progressBar}`} style={{ width: `${phaseProgress}%` }} />
            </div>
            <p className="text-xs text-slate-500 mt-1 text-right">{phaseProgress}%</p>
          </div>
          {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="p-5 space-y-4">
          {actions.map((action) => (
            <ActionItem key={action.id} action={action} meta={actionMeta[action.id]} status={statusOf(actionMeta, completed, action.id)} campuses={campuses}
              onToggle={onToggle} onMetaSaved={onMetaSaved} phaseConfig={config} toggling={toggling} />
          ))}
          {actions.length === 0 && <p className="text-center text-slate-400 py-4">No actions in this phase.</p>}
          <button onClick={() => onAddAction(index)} className="w-full border border-dashed border-slate-300 rounded-xl py-3 text-sm text-slate-500 hover:text-slate-800 hover:border-slate-400 flex items-center justify-center gap-2" data-testid={`add-action-phase-${index}`}>
            <Plus className="w-4 h-4" /> Add your own action to {phase.name}
          </button>
        </div>
      )}
    </div>
  );
}

export default function RoadmapPage() {
  const navigate = useNavigate();
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [completed, setCompleted] = useState([]);
  const [actionMeta, setActionMeta] = useState({});
  const [toggling, setToggling] = useState(null);
  const [noRoadmap, setNoRoadmap] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [addPhase, setAddPhase] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const applyRoadmap = (rm) => {
    setRoadmap(rm);
    setCompleted(rm.completed_actions || []);
    setActionMeta(rm.action_meta || {});
    setNoRoadmap(false);
  };

  const fetchRoadmap = useCallback(async () => {
    try {
      const [rmRes, campRes] = await Promise.all([getLatestRoadmap(), getCampuses().catch(() => ({ data: { campuses: [] } }))]);
      setCampuses(campRes.data.campuses || []);
      if (rmRes.data.status === 'active' && rmRes.data.roadmap) applyRoadmap(rmRes.data.roadmap);
      else setNoRoadmap(true);
    } catch (error) {
      setNoRoadmap(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoadmap(); }, [fetchRoadmap]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await generateRoadmap();
      applyRoadmap(response.data);
    } catch (error) {
      alert(formatApiError(error));
    } finally {
      setGenerating(false);
    }
  };

  const handleToggle = async (actionId) => {
    setToggling(actionId);
    try {
      const response = await toggleRoadmapAction(actionId);
      setCompleted(response.data.completed_actions);
      setActionMeta((prev) => ({ ...prev, [actionId]: { ...(prev[actionId] || {}), status: response.data.completed ? 'done' : 'not_started' } }));
    } catch (error) {
      console.error('Failed to toggle action:', error);
    } finally {
      setToggling(null);
    }
  };

  const handleMetaSaved = (actionId, meta, completedList) => {
    setActionMeta((prev) => ({ ...prev, [actionId]: meta }));
    setCompleted(completedList);
  };

  const handleActionAdded = ({ action, meta, phase_index }) => {
    setRoadmap((prev) => {
      const phases = prev.phases.map((p, i) => (i === phase_index ? { ...p, actions: [...(p.actions || []), action] } : p));
      return { ...prev, phases };
    });
    setActionMeta((prev) => ({ ...prev, [action.id]: meta }));
  };

  const handleEvidencePack = async () => {
    setDownloading(true);
    try { await downloadEvidencePack(); } catch (e) { alert('Could not generate the evidence pack.'); } finally { setDownloading(false); }
  };

  const allActions = roadmap ? roadmap.phases.flatMap((p) => p.actions || []) : [];
  const totalActions = allActions.length;
  const completedCount = allActions.filter((a) => statusOf(actionMeta, completed, a.id) === 'done').length;
  const overallProgress = totalActions > 0 ? Math.round((completedCount / totalActions) * 100) : 0;
  const overdueCount = allActions.filter((a) => isOverdue({ ...actionMeta[a.id], status: statusOf(actionMeta, completed, a.id) })).length;
  const unownedCount = allActions.filter((a) => !actionMeta[a.id]?.owner && statusOf(actionMeta, completed, a.id) !== 'done').length;
  const inProgressCount = allActions.filter((a) => statusOf(actionMeta, completed, a.id) === 'in_progress').length;

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-pulse text-slate-500">Loading roadmap...</div></div>;
  }

  if (noRoadmap && !generating) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all"><ArrowLeft className="w-5 h-5 text-slate-500" /></Link>
            <div>
              <h1 className="font-outfit text-xl font-bold text-slate-900">Improvement Roadmap</h1>
              <p className="text-sm text-slate-500">AI-powered improvement plan &amp; action tracker</p>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center"><Map className="w-10 h-10 text-blue-500" /></div>
            <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-3">No Roadmap Yet</h2>
            <p className="text-slate-500 mb-8 max-w-lg mx-auto">
              Generate an AI-powered improvement roadmap from your Balanced Scorecard, then assign owners and deadlines to every action and let AI flag drift.
            </p>
            <button onClick={handleGenerate} disabled={generating} className="bg-gradient-to-r from-blue-600 to-violet-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-violet-700 transition-all inline-flex items-center gap-2 disabled:opacity-50" data-testid="generate-roadmap-btn">
              {generating ? <><Loader2 className="w-5 h-5 animate-spin" /> Analyzing Scorecard...</> : <><Sparkles className="w-5 h-5" /> Generate Improvement Roadmap</>}
            </button>
            <p className="text-xs text-slate-400 mt-4">
              Requires a completed Balanced Scorecard. <button onClick={() => navigate('/ai-assessment')} className="text-blue-500 hover:underline">Take AI Assessment</button>
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all"><ArrowLeft className="w-5 h-5 text-slate-500" /></Link>
            <div className="min-w-0">
              <h1 className="font-outfit text-xl font-bold text-slate-900">Improvement Roadmap &amp; Action Tracker</h1>
              <p className="text-sm text-slate-500 truncate">{roadmap?.created_at ? `Generated ${new Date(roadmap.created_at).toLocaleDateString()}` : 'AI-Powered Action Plan'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to="/scorecard" className="hidden sm:flex px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg font-medium items-center gap-1.5"><BarChart3 className="w-4 h-4" /> Scorecard</Link>
            <button onClick={handleEvidencePack} disabled={downloading} className="px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 rounded-lg font-medium flex items-center gap-1.5 disabled:opacity-50" data-testid="roadmap-evidence-pack-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} <span className="hidden sm:inline">Evidence Pack</span>
            </button>
            <button onClick={handleGenerate} disabled={generating} className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg font-medium flex items-center gap-1.5 disabled:opacity-50" data-testid="regenerate-roadmap-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} <span className="hidden sm:inline">{generating ? 'Generating...' : 'Regenerate'}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {generating && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6 flex items-center gap-4 animate-pulse">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            <div>
              <p className="font-medium text-blue-900">Analyzing your Balanced Scorecard...</p>
              <p className="text-sm text-blue-700">AI is evaluating cross-category dependencies and building your phased roadmap.</p>
            </div>
          </div>
        )}

        {roadmap && !generating && (
          <>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="roadmap-progress">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Execution Progress</p>
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-outfit font-bold text-slate-900">{overallProgress}%</span>
                    <span className="text-sm text-slate-500">{completedCount} of {totalActions} actions done</span>
                  </div>
                </div>
                <div className="flex items-center gap-6" data-testid="tracker-stats">
                  <div className="text-center"><p className="text-lg font-outfit font-bold text-blue-600">{inProgressCount}</p><p className="text-xs text-slate-400">In progress</p></div>
                  <div className="text-center"><p className={`text-lg font-outfit font-bold ${overdueCount ? 'text-red-600' : 'text-slate-400'}`}>{overdueCount}</p><p className="text-xs text-slate-400">Overdue</p></div>
                  <div className="text-center"><p className={`text-lg font-outfit font-bold ${unownedCount ? 'text-amber-600' : 'text-slate-400'}`}>{unownedCount}</p><p className="text-xs text-slate-400">Unassigned</p></div>
                </div>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                {roadmap.phases.map((phase, idx) => {
                  const phaseActions = phase.actions?.length || 0;
                  const phaseCompleted = (phase.actions || []).filter((a) => statusOf(actionMeta, completed, a.id) === 'done').length;
                  const widthPct = totalActions > 0 ? (phaseActions / totalActions) * 100 : 0;
                  const fillPct = phaseActions > 0 ? (phaseCompleted / phaseActions) * 100 : 0;
                  return (
                    <div key={idx} style={{ width: `${widthPct}%` }} className="relative h-full">
                      <div className={`h-full ${PHASE_CONFIG[idx]?.progressBar || 'bg-slate-400'} transition-all duration-500`} style={{ width: `${fillPct}%` }} />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mb-6">
              <DriftPanel report={roadmap.drift_report} onUpdated={(r) => setRoadmap((prev) => ({ ...prev, drift_report: r }))} />
            </div>

            {(roadmap.overall_strategy || roadmap.cross_category_insights?.length > 0) && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="roadmap-strategy">
                {roadmap.overall_strategy && (
                  <div className="mb-4">
                    <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-blue-600" /> Strategy Overview</h3>
                    <p className="text-slate-600 leading-relaxed">{roadmap.overall_strategy}</p>
                  </div>
                )}
                {roadmap.cross_category_insights?.length > 0 && (
                  <div>
                    <h3 className="font-outfit text-base font-semibold text-slate-900 mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> Cross-Category Insights</h3>
                    <div className="space-y-2">
                      {roadmap.cross_category_insights.map((insight, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-slate-600"><ChevronRight className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /><span>{insight}</span></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-6">
              {roadmap.phases.map((phase, index) => (
                <PhaseSection key={index} phase={phase} index={index} actionMeta={actionMeta} completed={completed} campuses={campuses}
                  onToggle={handleToggle} onMetaSaved={handleMetaSaved} toggling={toggling} onAddAction={setAddPhase} />
              ))}
            </div>
          </>
        )}
      </main>

      {addPhase !== null && roadmap && (
        <AddActionModal phases={roadmap.phases} campuses={campuses} defaultPhase={addPhase} onClose={() => setAddPhase(null)} onAdded={handleActionAdded} />
      )}
    </div>
  );
}
